import {
  WSModal,
  WSModalCustomProps,
  WSText,
  useWSModalCustom
} from "@wingspanhq/fe-component-library";
import {
  MfaChallengeRequiredFor,
  MfaChallengeStatus,
  OrganizationSsoConfigStatus,
  PersonMfaFactor
} from "@wingspanhq/wingspan-api-sdk";
import { queriesMfa } from "@query";
import { MfaChallengeRequirement } from "@services/mfaService";
import { useSession } from "@hooks/useSession";
import React, { useState } from "react";
import { mfaCodeFieldForFactor } from "./mfaFactorLabel";
import { MfaCodeForm } from "./MfaCodeForm";
import { MfaFactorSelectForm } from "./MfaFactorSelectForm";

type Props = {
  requirement: MfaChallengeRequirement;
} & WSModalCustomProps<boolean>;

const ModalMfaChallenge: React.FC<Props> = props => {
  const { personId } = useSession();

  const factorsQuery = queriesMfa.listPersonMfaFactors.useQuery({ personId });

  const [selectedFactor, setSelectedFactor] = useState<PersonMfaFactor>();
  const [challengeId, setChallengeId] = useState<string>();

  const [createChallenge, createMeta] =
    queriesMfa.createMfaChallenge.useMutation({
      onSuccess: challenge => setChallengeId(challenge.id)
    });

  const [verifyChallenge, verifyMeta] =
    queriesMfa.verifyMfaChallenge.useMutation();

  const requiredFor =
    props.requirement.requiredFor ??
    MfaChallengeRequiredFor.HIGH_RISK_WRITE_ACTION;

  const factors = (factorsQuery.data?.data ?? []).filter(
    factor => factor.status === OrganizationSsoConfigStatus.ACTIVE
  );

  const startChallenge = (factor: PersonMfaFactor) => {
    setSelectedFactor(factor);
    createChallenge({
      mfaChallengeCreate: { requiredFor, factorId: factor.id }
    });
  };

  const handleSelectFactor = (factorId: string) => {
    const factor = factors.find(candidate => candidate.id === factorId);
    if (factor) {
      startChallenge(factor);
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (!selectedFactor || !challengeId) {
      return;
    }

    const result = await verifyChallenge({
      challengeId,
      mfaChallengeVerifyRequest: {
        factorId: selectedFactor.id,
        [mfaCodeFieldForFactor(selectedFactor)]: code
      }
    });

    if (result?.status === MfaChallengeStatus.VERIFIED) {
      props.onClose(true);
    }
  };

  const renderContent = () => {
    if (factorsQuery.isLoading) {
      return (
        <WSText color="gray600">Loading your verification methods…</WSText>
      );
    }

    if (factors.length === 0) {
      return (
        <WSText color="gray600">
          Additional verification is required, but no verification methods are
          set up for your account. Add one in your security settings to
          continue.
        </WSText>
      );
    }

    if (!challengeId || !selectedFactor) {
      return (
        <MfaFactorSelectForm
          factors={factors}
          submitting={createMeta.isLoading}
          onSubmit={handleSelectFactor}
        />
      );
    }

    return (
      <MfaCodeForm
        factor={selectedFactor}
        submitting={verifyMeta.isLoading}
        resending={createMeta.isLoading}
        error={verifyMeta.error}
        onSubmit={handleVerifyCode}
        onResend={() => startChallenge(selectedFactor)}
      />
    );
  };

  return (
    <WSModal
      title="Additional verification required"
      size="S"
      onClose={() => props.onClose(false)}
    >
      {renderContent()}
    </WSModal>
  );
};

export const useModalMfaChallenge = () => useWSModalCustom(ModalMfaChallenge);
